<template>
  <v-card class="teros-elevation" :color="tableColor">
    <v-card-title class="px-5 pb-0 text-uppercase v-title-cadastro">
      Relatório de Despesas ICT
    </v-card-title>
    <v-card-text>
      <div class="d-flex align-center pt-1">
        <v-autocomplete
          v-model="selectedClienteId"
          :items="clientes"
          item-value="id"
          item-text="razao_social"
          label="Empresa Parceira"
          dense
          outlined
          hide-details
          style="max-width: 300px"
          class="mr-3"
          @change="onClienteChange"
        />
        <v-autocomplete
          v-model="projetoId"
          :items="projetosFiltrados"
          item-value="id"
          item-text="label"
          label="Projeto"
          dense
          outlined
          hide-details
          style="max-width: 400px"
          class="mr-3"
          :disabled="!selectedClienteId && clientes.length > 0"
          @change="doLoad"
        />
        <div
          v-if="loaded"
          class="table-v-action-button mr-3 pt-1"
          @click="exportXlsx(arrayToPrint)"
        >
          <v-icon>mdi-file-excel-outline</v-icon>Exportar XLSX
        </div>
      </div>
    </v-card-text>

    <v-card-text v-if="loaded" style="padding: 0" class="rda-report">
      <!-- Summary and Antecipações -->
      <resumo-financeiro 
        :projeto="report.projeto" 
        :resumo="report.resumo" 
        :antecipacoes="report.antecipacoes"
      />

      <!-- Limits -->
      <controle-limites :limites="report.limites" />

      <!-- Despesas Details  -->
      <detalhes-rh :detalhes-rh="report.detalhes_rh" />
      <detalhes-viagens :detalhes="report.detalhes_viagens" />
      <detalhes-equipamentos :detalhes="report.detalhes_equipamentos" />
      <detalhes-servicos :detalhes="report.detalhes_servicos" />
      <detalhes-materiais :detalhes="report.detalhes_materiais" />
      <detalhes-custo-incorrido :detalhes="report.detalhes_custo_incorrido" />
      <detalhes-outros :detalhes="report.detalhes_outros" />
    </v-card-text>

    <v-card-text v-else-if="!loaded" class="pa-5 text-center grey--text">
      Selecione um projeto para visualizar o relatório.
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import ictRelatorioService from "@/api/ict/ictRelatorio.service";
import ictProjetosService from "@/api/ict/ictProjetos.service";
import ictClientesService from "@/api/ict/ictClientes.service";

import ResumoFinanceiro from "./components/resumo-financeiro.vue";
import ControleLimites from "./components/controle-limites.vue";
import DetalhesRh from "./components/detalhes-rh.vue";
import DetalhesViagens from "./components/detalhes-viagens.vue";
import DetalhesEquipamentos from "./components/detalhes-equipamentos.vue";
import DetalhesServicos from "./components/detalhes-servicos.vue";
import DetalhesMateriais from "./components/detalhes-materiais.vue";
import DetalhesCustoIncorrido from "./components/detalhes-custo-incorrido.vue";
import DetalhesOutros from "./components/detalhes-outros.vue";

export default {
  components: {
    ResumoFinanceiro,
    ControleLimites,
    DetalhesRh,
    DetalhesViagens,
    DetalhesEquipamentos,
    DetalhesServicos,
    DetalhesMateriais,
    DetalhesCustoIncorrido,
    DetalhesOutros,
  },
  computed: {
    ...mapGetters(["clientId"]),
    arrayToPrint() {
      if (!this.report) return [];

      const rows = [];
      const resumo = this.report.resumo;
      const antecipacoes = this.report.antecipacoes;
      
      // Resumo Header
      rows.push(["RESUMO FINANCEIRO", "Valor"]);
      
      const typeLabels = [
        ["rh_direto", "RH Direto"],
        ["rh_indireto", "RH Indireto"],
        ["viagens", "Viagens"],
        ["equipamentos", "Equipamentos"],
        ["softwares", "Softwares"],
        ["servicos_terceiros", "Serviços Terceiros"],
        ["material_de_consumo", "Material de Consumo"],
        ["custo_incorrido", "Custo Incorrido"],
        ["outros_correlatos", "Outros Correlatos"],
      ];

      typeLabels.forEach(([key, label]) => {
        rows.push([label, (resumo[key] || 0).toFixed(2)]);
      });
      rows.push(["TOTAL GASTO", resumo.total_despesas.toFixed(2)]);
      rows.push([]);

      // Antecipacoes
      if (antecipacoes) {
        rows.push(["ANTECIPAÇÕES", "Valor"]);
        rows.push(["Antecipação do Ano Anterior", (antecipacoes.valor_antecipado_ano_anterior || 0).toFixed(2)]);
        rows.push(["Valor Repassado no Ano-Base", (antecipacoes.valor_repassado_ano_base || 0).toFixed(2)]);
        rows.push(["Antecipação para o Ano Seguinte", (antecipacoes.valor_antecipado_ano_seguinte || 0).toFixed(2)]);
        rows.push([]);
      }

      // RH Details
      if (this.report.detalhes_rh && this.report.detalhes_rh.length > 0) {
        rows.push(["--- DETALHES RH ---"]);
        rows.push(["Tipo RH", "Participante", "CPF", "Escolaridade", "Horas Trabalhadas", "Valor", "Atuação"]);
        this.report.detalhes_rh.forEach((rh) => {
          rh.despesas.forEach((d) => {
            rows.push([
              rh.tipo_rh,
              rh.participante_nome,
              rh.participante_cpf,
              rh.nivel_escolaridade,
              rh.horas_trabalhadas,
              (d.valor || 0).toFixed(2),
              d.atuacao_projeto || "",
            ]);
          });
        });
        rows.push([]);
      }

      // Viagens Details
      if (this.report.detalhes_viagens && this.report.detalhes_viagens.length > 0) {
        rows.push(["--- DETALHES VIAGENS ---"]);
        rows.push(["Participante", "CPF", "Descrição", "Data Início", "Data Fim", "Valor"]);
        this.report.detalhes_viagens.forEach((d) => {
          rows.push([
            d.participante_nome,
            d.participante_cpf,
            d.descricao,
            d.data_inicio || "",
            d.data_fim || "",
            (d.valor || 0).toFixed(2),
          ]);
        });
        rows.push([]);
      }

      // Equipamentos Details
      if (this.report.detalhes_equipamentos && this.report.detalhes_equipamentos.length > 0) {
        rows.push(["--- DETALHES EQUIPAMENTOS E SOFTWARES ---"]);
        rows.push(["Tipo", "Descrição", "Tipo Apropriação", "Valor Depreciação", "Data Aquisição", "Valor"]);
        this.report.detalhes_equipamentos.forEach((d) => {
          rows.push([
            d.tipo_rubrica,
            d.descricao,
            d.tipo_apropriacao,
            (d.valor_depreciacao || 0).toFixed(2),
            d.data_aquisicao || "",
            (d.valor || 0).toFixed(2),
          ]);
        });
        rows.push([]);
      }

      // Servicos Details
      if (this.report.detalhes_servicos && this.report.detalhes_servicos.length > 0) {
        rows.push(["--- DETALHES SERVIÇOS TERCEIROS ---"]);
        rows.push(["Contratada", "CNPJ", "Descrição", "Data Início", "Data Fim", "Valor"]);
        this.report.detalhes_servicos.forEach((d) => {
          rows.push([
            d.contratada_razao_social,
            d.contratada_cnpj,
            d.descricao,
            d.data_inicio || "",
            d.data_fim || "",
            (d.valor || 0).toFixed(2),
          ]);
        });
        rows.push([]);
      }

      // Materiais Details
      if (this.report.detalhes_materiais && this.report.detalhes_materiais.length > 0) {
        rows.push(["--- DETALHES MATERIAL DE CONSUMO ---"]);
        rows.push(["Descrição", "Quantidade", "Valor"]);
        this.report.detalhes_materiais.forEach((d) => {
          rows.push([
            d.descricao,
            d.quantidade,
            (d.valor || 0).toFixed(2),
          ]);
        });
        rows.push([]);
      }

      // Custo Incorrido Details
      if (this.report.detalhes_custo_incorrido && this.report.detalhes_custo_incorrido.length > 0) {
        rows.push(["--- DETALHES CUSTO INCORRIDO ---"]);
        rows.push(["Descrição", "Justificativa", "Valor"]);
        this.report.detalhes_custo_incorrido.forEach((d) => {
          rows.push([
            d.descricao,
            d.justificativa,
            (d.valor || 0).toFixed(2),
          ]);
        });
        rows.push([]);
      }

      // Outros Details
      if (this.report.detalhes_outros && this.report.detalhes_outros.length > 0) {
        rows.push(["--- DETALHES OUTROS CORRELATOS ---"]);
        rows.push(["Descrição", "Justificativa", "Valor"]);
        this.report.detalhes_outros.forEach((d) => {
          rows.push([
            d.descricao,
            d.justificativa,
            (d.valor || 0).toFixed(2),
          ]);
        });
        rows.push([]);
      }

      return rows;
    },
    projetosFiltrados() {
      if (!this.selectedClienteId) return this.projetos;
      return this.projetos.filter(p => p.cliente_id === this.selectedClienteId);
    },
  },
  data() {
    return {
      selectedClienteId: null,
      projetoId: null,
      clientes: [],
      projetos: [],
      report: null,
      loaded: false,
    };
  },
  methods: {
    async loadClientes() {
      try {
        const res = await ictClientesService.getAll(this.clientId);
        this.clientes = res.data || res;
      } catch (e) {
        this.$notify({
          group: "geral",
          title: "Erro",
          text: "Erro ao carregar Empresas Parceiras.",
          type: "error",
        });
      }
    },
    async loadProjetos() {
      try {
        const res = await ictProjetosService.getAll(this.clientId);
        this.projetos = (res.data || res).map((p) => ({
          id: p.id,
          cliente_id: p.cliente_id,
          label: `${p.sigla_projeto || ""} — ${p.nome_projeto} (${p.ano_base})`,
        }));
      } catch (e) {
        this.$notify({
          group: "geral",
          title: "Erro",
          text: "Erro ao carregar projetos.",
          type: "error",
        });
      }
    },
    async doLoad() {
      if (!this.projetoId) return;

      try {
        this.report = await ictRelatorioService.getDespesas(
          this.clientId,
          this.projetoId
        );
        this.loaded = true;
      } catch (e) {
        this.$notify({
          group: "geral",
          title: "Erro",
          text: "Erro ao carregar relatório.",
          type: "error",
        });
      }
    },
    onClienteChange() {
      this.projetoId = null;
      this.report = null;
      this.loaded = false;
    },
  },
  watch: {
    clientId() {
      this.loaded = false;
      this.report = null;
      this.projetoId = null;
      this.selectedClienteId = null;
      this.loadClientes();
      this.loadProjetos();
    },
  },
  mounted() {
    this.loadClientes();
    this.loadProjetos();
  },
};
</script>

<style lang="scss" scoped src="./styles/report.scss"></style>

